import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";

import { environment } from "environments/environment";
import { SchoolContext, SchoolContextResponse } from "@core/models";
import { AuthenticationService } from "@core/services/auth";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";

/**
 * The schools and curricula the signed-in teacher can act in, and the switch
 * between them.
 *
 * The list is one of contexts rather than schools: a school where the teacher
 * works in two curricula is listed once per curriculum. The backend authorises
 * school and curriculum together against the same list this endpoint is built
 * from, so anything listed is switchable.
 */
@Injectable({ providedIn: "root" })
export class SchoolContextService {
  /**
   *
   * @param {HttpClient} _http
   * @param {AuthenticationService} _authenticationService
   */
  constructor(
    private _http: HttpClient,
    private _authenticationService: AuthenticationService
  ) {}

  /**
   * Contexts available to the signed-in teacher.
   */
  getSchoolContexts(): Observable<SchoolContext[]> {
    return this._http
      .get<SchoolContextResponse>(`${environment.apiUrl}/teacher/school-data`)
      .pipe(map((response) => response?.schools ?? []));
  }

  /**
   * Re-issues the session against `context`.
   *
   * `educationSystem` is sent only where the entry names a curriculum; omitting
   * it preserves the endpoint's original behaviour for the single-curriculum
   * schools that are the normal case.
   *
   * The response is a full login result whose token replaces the stored one -
   * the previous token is not revoked and keeps resolving to the previous
   * school.
   */
  switchTo(context: SchoolContext): Observable<any> {
    let params = new HttpParams();
    if (context?.curriculum) {
      params = params.set("educationSystem", context.curriculum);
    }

    return this._http
      .post<any>(
        `${environment.apiUrl}/switch-school/${context.schoolId}`,
        null,
        { params }
      )
      .pipe(
        tap((data) => this._authenticationService.applySwitchedSession(data))
      );
  }
}
